import type { Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';

/**
 * Stand-in learner data for the Learner list / Add or remove learners
 * screens. Generated deterministically at module load so search results
 * and pagination stay stable across renders and tests.
 *
 * Replace the entire module with the real `getLearnerCohort` GraphQL
 * query once it lands; the shape (Learner[]) stays identical.
 */

const FIRST_NAMES = [
  'Avery',
  'Blake',
  'Cameron',
  'Dakota',
  'Elliot',
  'Finley',
  'Gray',
  'Hayden',
  'Indigo',
  'Jordan',
  'Kai',
  'Logan',
  'Morgan',
  'Noa',
  'Oakley',
  'Parker',
  'Quinn',
  'River',
  'Sage',
  'Taylor',
  'Umi',
  'Vesper',
  'Wren',
  'Xen',
  'Yael',
  'Zion',
];

const LAST_NAMES = [
  'Anderson',
  'Brooks',
  'Carter',
  'Davies',
  'Ellis',
  'Foster',
  'Garcia',
  'Hughes',
  'Iqbal',
  'Jensen',
  'Kim',
  'Lopez',
  'Mitchell',
  'Nguyen',
  'O’Connor',
  'Patel',
  'Quintero',
  'Reyes',
  'Singh',
  'Tanaka',
  'Underwood',
  'Vasquez',
  'Williams',
  'Xu',
  'Young',
  'Zhang',
];

// Deterministic PRNG (mulberry32) so the generated set is identical across
// renders/tests without depending on Math.random.
const seededRandom = (seed: number) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const POOL_SIZE = 300;
const DEFAULT_COHORT_SIZE = 150;

const generateLearner = (index: number, random: () => number): Learner => {
  const first = FIRST_NAMES[Math.floor(random() * FIRST_NAMES.length)] ?? 'Avery';
  const last = LAST_NAMES[Math.floor(random() * LAST_NAMES.length)] ?? 'Anderson';
  return {
    id: `learner-${index}`,
    name: `${first} ${last}`,
    email: `${first.toLowerCase()}.${last.toLowerCase().replace(/[^a-z]/g, '')}.${index}@example.com`,
  };
};

const generatePool = (): Learner[] => {
  const random = seededRandom(0xc05e7a);
  const seen = new Set<string>();
  const pool: Learner[] = [];
  // Generate slightly more than POOL_SIZE so we can drop occasional name
  // duplicates and still hit the target count.
  let i = 0;
  while (pool.length < POOL_SIZE) {
    const learner = generateLearner(i, random);
    if (!seen.has(learner.name)) {
      seen.add(learner.name);
      pool.push(learner);
    }
    i += 1;
  }
  return pool;
};

/** Full org-pool learners the editor can search across. */
export const MOCK_ORG_LEARNERS: Learner[] = generatePool();

/**
 * Default AI-curated cohort the modal opens with (the "original" list
 * Review-and-send reflects before any manual edits).
 */
export const getDefaultAiCohort = (): Learner[] => MOCK_ORG_LEARNERS.slice(0, DEFAULT_COHORT_SIZE);
